import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams, useOutletContext } from 'react-router-dom';
import { 
  ArrowLeft,
  Send,
  Mail,
  Users,
  X,
  AlertCircle,
  Loader,
  Check,
  Search,
  ChevronDown,
  ChevronUp,
  User,
  Paperclip,
  Eye,
  FileText,
  Palette
} from 'lucide-react';
import { supabase } from '../../utils/supabaseClient';
import RichTextEditor, { normalizeSpaces } from '../ui/RichTextEditor';
import '../../styles/CuratorContactEmail.css';

/**
 * CuratorContactEmail
 * 
 * Full page for composing and sending emails to selected contacts.
 * Supports:
 * - Single or multiple recipients
 * - Subject and body composition
 * - Recipient management (add/remove)
 * - Preview before sending
 * - Templates when collection context is provided
 */

const CuratorContactEmail = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { selectedOrganization } = useOutletContext() || {};
  
  // Get initial contact IDs from URL
  const initialIds = searchParams.get('ids')?.split(',').filter(Boolean) || [];
  
  // Get artist context from URL (from artist detail page)
  const artistIdParam = searchParams.get('artistId');
  const artistEmailParam = searchParams.get('email');
  const artistNameParam = searchParams.get('name');
  const collectionIdParam = searchParams.get('collectionId');
  const collectionNameParam = searchParams.get('collectionName');
  
  // Determine if we're in single artist mode with collection context
  const isSingleArtistMode = !!(artistEmailParam && collectionIdParam);
  
  // Data state
  const [allContacts, setAllContacts] = useState([]);
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  
  // Form state
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [formErrors, setFormErrors] = useState({});
  
  // Template state for single artist mode
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [showTemplates, setShowTemplates] = useState(false);
  
  // UI state
  const [showContactPicker, setShowContactPicker] = useState(false);
  const [contactSearch, setContactSearch] = useState('');
  const [showPreview, setShowPreview] = useState(false);

  // Load templates for collection
  const loadTemplates = async () => {
    if (!collectionIdParam) return;
    
    try {
      const { data, error: fetchError } = await supabase
        .from('email_templates')
        .select('*')
        .eq('collection_id', collectionIdParam)
        .order('name', { ascending: true });
      
      if (fetchError) throw fetchError;
      setTemplates(data || []);
    } catch (err) {
      console.error('Error loading templates:', err);
    }
  };

  // Load all contacts for the organization
  const loadContacts = async () => {
    if (!selectedOrganization?.organization_id) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      
      // If in single artist mode, just set the artist as the contact
      if (isSingleArtistMode) {
        setSelectedContacts([{
          id: artistIdParam || 'artist-1',
          first_name: artistNameParam || '',
          last_name: '',
          email: artistEmailParam
        }]);
        setLoading(false);
        return;
      }
      
      const { data, error: fetchError } = await supabase
        .from('external_organization_members')
        .select('id, first_name, last_name, email, additional_emails, membership_status')
        .eq('organization_id', selectedOrganization.organization_id)
        .order('last_name', { ascending: true });

      if (fetchError) throw fetchError;

      setAllContacts(data || []);
      
      // Select initial contacts from URL params
      if (initialIds.length > 0) {
        const initialContacts = (data || []).filter(c => 
          initialIds.includes(String(c.id))
        );
        setSelectedContacts(initialContacts);
      }

    } catch (err) {
      console.error('Error loading contacts:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadContacts();
    if (isSingleArtistMode) {
      loadTemplates();
    }
  }, [selectedOrganization?.organization_id]);

  // Apply template
  const applyTemplate = (template) => {
    if (!template) return;
    
    let templateSubject = template.subject || '';
    let templateBody = template.body || '';
    
    // Replace merge variables
    const artistName = artistNameParam || selectedContacts[0]?.first_name || 'Artist';
    const replacements = {
      '{artist_name}': artistName,
      '{collection_name}': collectionNameParam || '',
      '{organization_name}': selectedOrganization?.name || ''
    };
    
    Object.entries(replacements).forEach(([key, value]) => {
      templateSubject = templateSubject.replace(new RegExp(key, 'g'), value);
      templateBody = templateBody.replace(new RegExp(key, 'g'), value);
    });
    
    setSubject(templateSubject);
    setBody(templateBody);
    setSelectedTemplate(template);
    setShowTemplates(false);
  };

  // Filter contacts for picker
  const filteredContacts = useMemo(() => {
    if (!contactSearch.trim()) return allContacts;
    
    const query = contactSearch.toLowerCase();
    return allContacts.filter(c => 
      c.first_name?.toLowerCase().includes(query) ||
      c.last_name?.toLowerCase().includes(query) ||
      c.email?.toLowerCase().includes(query)
    );
  }, [allContacts, contactSearch]);

  // Get contacts not yet selected
  const availableContacts = useMemo(() => {
    const selectedIds = new Set(selectedContacts.map(c => c.id));
    return filteredContacts.filter(c => !selectedIds.has(c.id));
  }, [filteredContacts, selectedContacts]);

  // Add contact to selection
  const addContact = (contact) => {
    setSelectedContacts(prev => [...prev, contact]);
  };

  // Remove contact from selection
  const removeContact = (contactId) => {
    setSelectedContacts(prev => prev.filter(c => c.id !== contactId));
  };

  // Select all visible contacts
  const selectAllVisible = () => {
    const selectedIds = new Set(selectedContacts.map(c => c.id));
    const newContacts = filteredContacts.filter(c => !selectedIds.has(c.id));
    setSelectedContacts(prev => [...prev, ...newContacts]);
  };

  // Clear all selected
  const clearAllSelected = () => {
    setSelectedContacts([]);
  };

  // Validate form
  const validateForm = () => {
    const errors = {};
    
    if (selectedContacts.length === 0) {
      errors.recipients = 'Please select at least one recipient';
    }
    if (!subject.trim()) {
      errors.subject = 'Subject is required';
    }
    if (!body.trim()) {
      errors.body = 'Message body is required';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Get all recipient emails
  const getAllRecipientEmails = () => {
    const emails = [];
    selectedContacts.forEach(contact => {
      if (contact.email) {
        emails.push({
          email: contact.email,
          name: `${contact.first_name} ${contact.last_name}`.trim()
        });
      }
    });
    return emails;
  };

  // Send email
  const handleSend = async () => {
    if (!validateForm()) return;

    try {
      setSending(true);
      setError(null);

      const recipients = getAllRecipientEmails();
      
      // Call the edge function to send emails
      const { data, error: sendError } = await supabase.functions.invoke('send-contact-email', {
        body: {
          recipients,
          subject: subject.trim(),
          body: normalizeSpaces(body.trim()),
          isHtml: true,
          organizationId: selectedOrganization.organization_id,
          organizationName: selectedOrganization?.name || 'Your Organization'
        }
      });

      if (sendError) throw sendError;

      setSuccessMessage(`Email sent to ${recipients.length} recipient${recipients.length !== 1 ? 's' : ''}`);
      
      // Clear form after short delay
      setTimeout(() => {
        navigate('/curator/contacts');
      }, 2000);

    } catch (err) {
      console.error('Error sending email:', err);
      setError(err.message || 'Failed to send email. Please try again.');
    } finally {
      setSending(false);
    }
  };

  // Get initials for avatar
  const getInitials = (contact) => {
    const first = contact.first_name?.[0] || '';
    const last = contact.last_name?.[0] || '';
    return (first + last).toUpperCase() || '?';
  };

  if (loading) {
    return (
      <div className="curator-contact-email">
        <div className="loading-state">
          <Loader size={32} className="spin" />
          <p>Loading contacts...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="curator-contact-email">
      {/* Header */}
      <div className="email-header">
        <button className="back-button" onClick={() => navigate(isSingleArtistMode ? -1 : '/curator/contacts')}>
          <ArrowLeft size={20} />
          <span>{isSingleArtistMode ? 'Back' : 'Contacts'}</span>
        </button>
        
        <h1>{isSingleArtistMode ? `Email to ${artistNameParam || artistEmailParam}` : 'Compose Email'}</h1>
        
        <div className="header-actions">
          <button 
            className="btn-secondary"
            onClick={() => setShowPreview(!showPreview)}
          >
            <Eye size={16} />
            {showPreview ? 'Edit' : 'Preview'}
          </button>
          
          <button 
            className="btn-primary"
            onClick={handleSend}
            disabled={sending || selectedContacts.length === 0}
          >
            {sending ? (
              <>
                <Loader size={16} className="spin" />
                Sending...
              </>
            ) : (
              <>
                <Send size={16} />
                Send Email
              </>
            )}
          </button>
        </div>
      </div>

      {/* Success/Error Messages */}
      {successMessage && (
        <div className="success-banner">
          <Check size={16} />
          {successMessage}
        </div>
      )}
      
      {error && (
        <div className="error-banner">
          <AlertCircle size={16} />
          {error}
          <button onClick={() => setError(null)}>
            <X size={14} />
          </button>
        </div>
      )}

      <div className="email-content">
        {/* Recipients Section */}
        <div className="email-section recipients-section">
          <div className="section-header">
            <div className="section-title">
              <Users size={18} />
              <h2>Recipients</h2>
              <span className="recipient-count">{selectedContacts.length} selected</span>
            </div>
            <button 
              className="add-recipients-btn"
              onClick={() => setShowContactPicker(!showContactPicker)}
            >
              {showContactPicker ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              {showContactPicker ? 'Hide' : 'Add Recipients'}
            </button>
          </div>
          
          {formErrors.recipients && (
            <div className="inline-error">{formErrors.recipients}</div>
          )}

          {/* Selected Recipients */}
          <div className="selected-recipients">
            {selectedContacts.length === 0 ? (
              <div className="no-recipients">
                <Mail size={24} />
                <p>No recipients selected. Click "Add Recipients" to choose contacts.</p>
              </div>
            ) : (
              <div className="recipient-chips">
                {selectedContacts.slice(0, 5).map(contact => (
                  <div key={contact.id} className="recipient-chip">
                    <div className="chip-avatar">
                      {getInitials(contact)}
                    </div>
                    <div className="chip-info">
                      <span className="chip-name">{contact.first_name} {contact.last_name}</span>
                      <span className="chip-email">{contact.email}</span>
                    </div>
                    <button 
                      className="chip-remove"
                      onClick={() => removeContact(contact.id)}
                      title="Remove recipient"
                    >
                      <X size={14} />
                    </button>
                  </div>
                ))}
                {selectedContacts.length > 5 && (
                  <div className="recipient-chip more-chip">
                    <span className="more-count">+{selectedContacts.length - 5} more</span>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Contact Picker */}
          {showContactPicker && (
            <div className="contact-picker">
              <div className="picker-search">
                <Search size={16} />
                <input
                  type="text"
                  placeholder="Search contacts..."
                  value={contactSearch}
                  onChange={(e) => setContactSearch(e.target.value)}
                />
                {contactSearch && (
                  <button className="clear-search" onClick={() => setContactSearch('')}>
                    <X size={14} />
                  </button>
                )}
              </div>
              
              <div className="picker-actions">
                <button className="picker-action-btn" onClick={selectAllVisible}>
                  Select All ({availableContacts.length})
                </button>
                {selectedContacts.length > 0 && (
                  <button className="picker-action-btn danger" onClick={clearAllSelected}>
                    Clear All
                  </button>
                )}
              </div>

              <div className="picker-list">
                {availableContacts.length === 0 ? (
                  <div className="picker-empty">
                    {contactSearch ? 'No matching contacts found' : 'All contacts selected'}
                  </div>
                ) : (
                  availableContacts.slice(0, 50).map(contact => (
                    <div 
                      key={contact.id} 
                      className="picker-item"
                      onClick={() => addContact(contact)}
                    >
                      <div className="picker-avatar">
                        {getInitials(contact)}
                      </div>
                      <div className="picker-info">
                        <span className="picker-name">{contact.first_name} {contact.last_name}</span>
                        <span className="picker-email">{contact.email}</span>
                      </div>
                      <span className={`picker-status status-${contact.membership_status}`}>
                        {contact.membership_status}
                      </span>
                    </div>
                  ))
                )}
                {availableContacts.length > 50 && (
                  <div className="picker-more">
                    + {availableContacts.length - 50} more contacts (use search to filter)
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Email Composition */}
        {showPreview ? (
          <div className="email-preview">
            <div className="preview-header">
              <h3>Email Preview</h3>
            </div>
            <div className="preview-content">
              <div className="preview-field">
                <span className="preview-label">To:</span>
                <span className="preview-value">
                  {selectedContacts.map(c => `${c.first_name} ${c.last_name} <${c.email}>`).join(', ')}
                </span>
              </div>
              <div className="preview-field">
                <span className="preview-label">Subject:</span>
                <span className="preview-value">{subject || '(No subject)'}</span>
              </div>
              <div
                className="preview-body"
                dangerouslySetInnerHTML={{ __html: body || '<p>(No message)</p>' }}
              />
            </div>
          </div>
        ) : (
          <div className="email-compose">
            {/* Templates Panel for single artist mode */}
            {isSingleArtistMode && templates.length > 0 && (
              <div className="templates-panel">
                <div className="templates-header" onClick={() => setShowTemplates(!showTemplates)}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <FileText size={16} />
                    <span style={{ fontWeight: 500 }}>Use a Template</span>
                    {selectedTemplate && (
                      <span style={{ fontSize: '0.75rem', color: '#10b981', marginLeft: '0.5rem' }}>
                        ({selectedTemplate.name} applied)
                      </span>
                    )}
                  </div>
                  {showTemplates ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                </div>
                {showTemplates && (
                  <div className="templates-list">
                    {templates.map(template => (
                      <button
                        key={template.id}
                        className={`template-item ${selectedTemplate?.id === template.id ? 'selected' : ''}`}
                        onClick={() => applyTemplate(template)}
                      >
                        <Palette size={14} />
                        <span>{template.name}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
            
            {/* Collection context banner */}
            {isSingleArtistMode && collectionNameParam && (
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '0.5rem', 
                padding: '0.75rem 1rem', 
                background: '#f0fdf4', 
                border: '1px solid #bbf7d0', 
                borderRadius: '8px', 
                marginBottom: '1rem',
                fontSize: '0.875rem',
                color: '#166534'
              }}>
                <Palette size={16} />
                <span>Sending from collection: <strong>{collectionNameParam}</strong></span>
              </div>
            )}

            <div className="compose-field">
              <label htmlFor="subject">Subject</label>
              <input
                id="subject"
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="Enter email subject..."
                className={formErrors.subject ? 'input-error' : ''}
              />
              {formErrors.subject && <span className="field-error">{formErrors.subject}</span>}
            </div>
            
            <div className="compose-field">
              <label htmlFor="body">Message</label>
              <RichTextEditor
                value={body}
                onChange={setBody}
                placeholder="Write your message here..."
                className={formErrors.body ? 'input-error' : ''}
                minHeight="300px"
              />
              {formErrors.body && <span className="field-error">{formErrors.body}</span>}
            </div>

            <div className="compose-footer">
              <div className="compose-tips">
                <p>
                  <strong>Tip:</strong> Emails will be sent individually to each recipient. 
                  They will not see other recipients' addresses.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CuratorContactEmail;
